import { useState, useEffect, useRef, useCallback } from 'react';
import HelixLoader from '../HelixLoader/HelixLoader';
import './UploadProgressToast.css';

const DURATION_MS = 20_000; // 20s ramp to max
const MAX_PERCENT = 85;
const COMPLETE_HOLD_MS = 1200; // hold at 100% before auto-dismiss

/**
 * Ease-out cubic: fast start, decelerates toward MAX_PERCENT.
 * At 5s → ~54%, 10s → ~75%, 15s → ~82%, 20s → 85%, then pauses.
 */
function easedPercent(elapsed) {
    const t = Math.min(elapsed / DURATION_MS, 1);
    const eased = 1 - Math.pow(1 - t, 3);
    return eased * MAX_PERCENT;
}

/** Single toast item — manages its own animation loop independently. */
function UploadProgressItem({ progress, onDismiss }) {
    const [percent, setPercent] = useState(0);
    const [visible, setVisible] = useState(false);
    const rafRef = useRef(null);
    const dismissTimerRef = useRef(null);

    const dismiss = useCallback(() => {
        setVisible(false);
        setTimeout(() => onDismiss(progress.id), 300);
    }, [onDismiss, progress.id]);

    // Animate progress while uploading
    useEffect(() => {
        if (progress.status !== 'uploading') {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            return;
        }

        setVisible(true);
        const { startTime } = progress;

        function tick() {
            setPercent(easedPercent(Date.now() - startTime));
            rafRef.current = requestAnimationFrame(tick);
        }
        rafRef.current = requestAnimationFrame(tick);

        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [progress.status, progress.startTime]);

    // Snap to 100% on complete, then auto-dismiss
    useEffect(() => {
        if (progress.status === 'complete') {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            setPercent(100);
            dismissTimerRef.current = setTimeout(dismiss, COMPLETE_HOLD_MS);
        }

        if (progress.status === 'error') {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        }

        return () => { if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current); };
    }, [progress.status, dismiss]);

    const isError = progress.status === 'error';
    const isComplete = progress.status === 'complete';
    const isUploading = progress.status === 'uploading';

    const label = isError
        ? (progress.message || 'Upload failed')
        : isComplete
            ? `${progress.fileName} — complete`
            : `${progress.fileName} — analyzing\u2026`;

    return (
        <div className={`upload-progress-toast ${visible ? 'visible' : ''} ${isError ? 'error' : ''}`}>
            <div className="upload-progress-helix">
                {isUploading && <HelixLoader size={44} color="rgba(255,255,255,0.7)" dotCount={12} gap={1} />}
                {isComplete && <span className="upload-progress-icon complete">{'\u2713'}</span>}
                {isError && <span className="upload-progress-icon error">{'\u2717'}</span>}
            </div>
            <div className="upload-progress-content">
                <div className="upload-progress-label">
                    <span className="upload-progress-filename">{label}</span>
                </div>
                <div className="upload-progress-track">
                    <div
                        className={`upload-progress-bar ${isError ? 'error' : ''} ${isComplete ? 'complete' : ''}`}
                        style={{ width: `${percent}%` }}
                    />
                </div>
                {isError && (
                    <div className="upload-progress-error-footer">
                        <span className="upload-progress-error-file">{progress.fileName}</span>
                        <button className="upload-progress-dismiss" onClick={() => onDismiss(progress.id)}>
                            Dismiss
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Container that renders a stack of upload progress toasts.
 *
 * Props:
 *   uploadProgresses: Array<{ id, status, startTime, fileName, message? }>
 *   onDismiss: (id: number) => void
 */
export default function UploadProgressToast({ uploadProgresses, onDismiss }) {
    if (!uploadProgresses?.length) return null;

    return (
        <div className="upload-progress-stack">
            {uploadProgresses.map(progress => (
                <UploadProgressItem
                    key={progress.id}
                    progress={progress}
                    onDismiss={onDismiss}
                />
            ))}
        </div>
    );
}
